/**
 * PaymentMethodModal Component
 * 
 * Modal para seleção de forma de recebimento ao finalizar venda.
 * 
 * Funcionalidades:
 * - Lista formas de recebimento cadastradas
 * - Seleção de uma ou múltiplas formas de pagamento
 * - Campo para valor de cada forma (se parcial)
 * - Validação de valor total
 * - Suporte a múltiplas formas de pagamento
 * - Identificação de formas que geram conciliação (PagSeguro)
 */

import { useState, useEffect } from 'react'
import { CreditCard, DollarSign, Wallet, AlertCircle, X, ChevronDown, ChevronRight } from 'lucide-react'
import { paymentReceiptMethodService } from '../../../services/erpApi'
import ConfirmModal from '../ConfirmModal/ConfirmModal'
import '../Modal/Modal.css'
import './PaymentMethodModal.css'

function PaymentMethodModal({
  isOpen,
  onClose,
  onConfirm,
  totalAmount,
  required = true
}) {
  const [paymentMethods, setPaymentMethods] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [selectedPayments, setSelectedPayments] = useState([])
  const [authorizationNumbers, setAuthorizationNumbers] = useState({}) // { paymentId: authorizationNumber }
  const [selectedProvider, setSelectedProvider] = useState(null)
  // Estados para pagamento em dinheiro
  const [cashReceived, setCashReceived] = useState('') // Valor recebido do cliente
  const [cashToDiscount, setCashToDiscount] = useState('') // Valor a descontar (usado para pagar)
  // Estado para modal de confirmação
  const [confirmCancel, setConfirmCancel] = useState(false)

  // Carregar formas de recebimento
  useEffect(() => {
    if (isOpen && !loading) {
      loadPaymentMethods()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  const loadPaymentMethods = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await paymentReceiptMethodService.getAll({ active: true })
      if (response.success) {
        setPaymentMethods(response.data || [])
      } else {
        setError('Erro ao carregar formas de recebimento')
      }
    } catch (err) {
      console.error('Erro ao carregar formas de recebimento:', err)
      setError('Erro ao carregar formas de recebimento')
    } finally {
      setLoading(false)
    }
  }

  // Formatar valor monetário
  // Aceita número (já em reais) ou string (pode ser centavos ou reais)
  const formatCurrency = (value) => {
    // Se for número, já está em reais - formatar diretamente
    if (typeof value === 'number') {
      return value.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }
    
    // Se for string, verificar se parece ser um número decimal (tem ponto ou vírgula)
    const stringValue = String(value)
    if (stringValue.includes('.') || stringValue.includes(',')) {
      // Já está em formato decimal (reais), converter e formatar
      const numValue = parseFloat(stringValue.replace(/\./g, '').replace(',', '.')) || 0
      return numValue.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    }
    
    // Se não tem ponto/vírgula, tratar como centavos (comportamento antigo)
    const numbers = stringValue.replace(/\D/g, '')
    const cents = parseInt(numbers) || 0
    const reais = (cents / 100).toFixed(2)
    return parseFloat(reais).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  // Converter string formatada para número
  const parseCurrency = (value) => {
    return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0
  }

  // Verificar se método é dinheiro
  const isCashMethod = (method) => {
    return method.provider === 'DINHEIRO' || method.name.toLowerCase().includes('dinheiro')
  }

  // Adicionar forma de pagamento
  const handleAddPayment = (method) => {
    const existing = selectedPayments.find(p => p.id === method.id)
    if (existing) return

    const isCash = isCashMethod(method)

    // Calcular valor já preenchido nos outros métodos (exceto dinheiro)
    const totalFilled = selectedPayments
      .filter(p => {
        const pMethod = paymentMethods.find(m => m.id === p.id)
        return pMethod && !isCashMethod(pMethod)
      })
      .reduce((sum, p) => sum + parseCurrency(p.amount), 0)
    
    const remaining = totalAmount - totalFilled

    // Se já houver métodos selecionados, preencher automaticamente com o restante
    // Caso contrário, preencher com o valor total
    let initialAmount = '0,00'
    if (isCash) {
      // Para dinheiro, inicializar com valor restante (valor a descontar)
      initialAmount = remaining > 0 ? formatCurrency(remaining) : '0,00'
      // Inicializar valor recebido como vazio
      setCashReceived('')
      setCashToDiscount(initialAmount)
    } else {
      if (selectedPayments.length === 0) {
        // Primeiro método: preencher com o valor total
        initialAmount = totalAmount > 0 ? formatCurrency(totalAmount) : '0,00'
      } else if (remaining > 0) {
        // Métodos subsequentes: preencher com o valor restante
        initialAmount = formatCurrency(remaining)
      }
    }

    setSelectedPayments([
      ...selectedPayments,
      {
        id: method.id,
        name: method.name,
        requiresConfirmation: method.requires_confirmation,
        amount: initialAmount
      }
    ])
    
    // Inicializar número de autorização vazio para este pagamento (apenas se não for dinheiro)
    if (!isCash) {
      setAuthorizationNumbers(prev => ({
        ...prev,
        [method.id]: ''
      }))
    }
  }

  // Remover forma de pagamento
  const handleRemovePayment = (id) => {
    const method = paymentMethods.find(m => m.id === id)
    const isCash = method && isCashMethod(method)
    
    setSelectedPayments(selectedPayments.filter(p => p.id !== id))
    
    // Remover também o número de autorização associado (se não for dinheiro)
    if (!isCash) {
      setAuthorizationNumbers(prev => {
        const newAuth = { ...prev }
        delete newAuth[id]
        return newAuth
      })
    }
    
    // Limpar estados de dinheiro se remover o método dinheiro
    if (isCash) {
      setCashReceived('')
      setCashToDiscount('')
    }
  }

  // Atualizar número de autorização
  const handleUpdateAuthorizationNumber = (paymentId, value) => {
    // Limitar a 100 caracteres e permitir apenas alfanumérico, espaços, hífens e underscores
    const cleaned = value.replace(/[^A-Z0-9\s\-_]/gi, '').slice(0, 100)
    setAuthorizationNumbers(prev => ({
      ...prev,
      [paymentId]: cleaned
    }))
  }

  // Formatar valor para exibição (aceita entrada livre)
  const formatCurrencyInput = (value) => {
    if (!value || value === '') return ''
    
    // Remove tudo exceto números e vírgula
    let cleaned = value.replace(/[^\d,]/g, '')
    
    // Garantir apenas uma vírgula
    const parts = cleaned.split(',')
    let formatted = parts[0] || ''
    
    // Se tiver vírgula, adicionar parte decimal (máximo 2 dígitos)
    if (parts.length > 1) {
      formatted += ',' + parts.slice(1).join('').substring(0, 2)
    }
    
    return formatted
  }

  // Atualizar valor de uma forma de pagamento
  const handleUpdateAmount = (id, value) => {
    // Permitir edição livre - formatar apenas a entrada
    const formatted = formatCurrencyInput(value)
    setSelectedPayments(selectedPayments.map(p => 
      p.id === id ? { ...p, amount: formatted } : p
    ))
  }

  // Formatar valor ao sair do campo (onBlur)
  const handleAmountBlur = (id, value) => {
    if (!value || value === '') {
      // Se vazio, definir como 0,00
      setSelectedPayments(selectedPayments.map(p => 
        p.id === id ? { ...p, amount: '0,00' } : p
      ))
      return
    }
    
    // Converter para número e formatar corretamente
    const numValue = parseCurrency(value)
    const formatted = formatCurrency(numValue)
    setSelectedPayments(selectedPayments.map(p => 
      p.id === id ? { ...p, amount: formatted } : p
    ))
  }

  // Sugerir preenchimento automático do último método com o valor restante
  const handleAutoFillRemaining = () => {
    if (selectedPayments.length === 0) return

    const totalFilled = selectedPayments.reduce((sum, p) => sum + parseCurrency(p.amount), 0)
    const remaining = totalAmount - totalFilled

    if (remaining > 0) {
      // Preencher o último método adicionado com o valor restante
      const lastPayment = selectedPayments[selectedPayments.length - 1]
      const formatted = formatCurrency(remaining)
      setSelectedPayments(selectedPayments.map(p => 
        p.id === lastPayment.id ? { ...p, amount: formatted } : p
      ))
    }
  }

  // Calcular total dos pagamentos
  const calculateTotalPayments = () => {
    // Para dinheiro, usar o valor a descontar, não o valor recebido
    let total = 0
    selectedPayments.forEach(p => {
      const method = paymentMethods.find(m => m.id === p.id)
      if (method && isCashMethod(method)) {
        // Para dinheiro, usar cashToDiscount se estiver preenchido, senão usar amount
        const cashDiscount = cashToDiscount ? parseCurrency(cashToDiscount) : parseCurrency(p.amount)
        total += cashDiscount
      } else {
        total += parseCurrency(p.amount)
      }
    })
    return total
  }

  // Calcular diferença
  const calculateDifference = () => {
    return totalAmount - calculateTotalPayments()
  }

  // Calcular troco para dinheiro
  const calculateCashChange = () => {
    const cashReceivedValue = parseCurrency(cashReceived)
    const cashDiscountValue = cashToDiscount ? parseCurrency(cashToDiscount) : 0
    
    if (cashReceivedValue > 0 && cashDiscountValue > 0 && cashReceivedValue > cashDiscountValue) {
      return cashReceivedValue - cashDiscountValue
    }
    
    return null
  }

  // Atualizar valor recebido em dinheiro
  const handleCashReceivedChange = (value) => {
    const formatted = formatCurrencyInput(value)
    setCashReceived(formatted)
  }

  // Atualizar valor a descontar em dinheiro
  const handleCashToDiscountChange = (value) => {
    const formatted = formatCurrencyInput(value)
    setCashToDiscount(formatted)
    
    // Atualizar também o amount do pagamento em dinheiro
    const cashPayment = selectedPayments.find(p => {
      const method = paymentMethods.find(m => m.id === p.id)
      return method && isCashMethod(method)
    })
    
    if (cashPayment) {
      const finalValue = formatted || '0,00'
      setSelectedPayments(selectedPayments.map(p => 
        p.id === cashPayment.id ? { ...p, amount: finalValue } : p
      ))
    }
  }

  // Preencher valor a descontar com o restante automaticamente
  const handleAutoFillCashDiscount = () => {
    // Calcular o valor restante após outros pagamentos (se houver)
    const otherPayments = selectedPayments
      .filter(p => {
        const method = paymentMethods.find(m => m.id === p.id)
        return method && !isCashMethod(method)
      })
      .reduce((sum, p) => sum + parseCurrency(p.amount), 0)
    
    const remainingAfterOtherPayments = totalAmount - otherPayments
    const exactAmount = Math.max(0, remainingAfterOtherPayments)
    
    const formatted = formatCurrency(exactAmount)
    setCashToDiscount(formatted)
    
    // Atualizar também o amount do pagamento em dinheiro
    const cashPayment = selectedPayments.find(p => {
      const method = paymentMethods.find(m => m.id === p.id)
      return method && isCashMethod(method)
    })
    
    if (cashPayment) {
      setSelectedPayments(selectedPayments.map(p => 
        p.id === cashPayment.id ? { ...p, amount: formatted } : p
      ))
    }
  }

  // Obter ícone da forma de pagamento
  const getPaymentIcon = (name) => {
    const nameLower = name.toLowerCase()
    if (nameLower.includes('dinheiro') || nameLower.includes('cash')) {
      return DollarSign
    }
    if (nameLower.includes('cartão') || nameLower.includes('card') || nameLower.includes('pagseguro')) {
      return CreditCard
    }
    return Wallet
  }

  // Agrupar métodos por provider
  const groupMethodsByProvider = () => {
    const grouped = {}
    
    paymentMethods.forEach(method => {
      const provider = method.provider || 'OUTROS'
      
      if (!grouped[provider]) {
        grouped[provider] = []
      }
      
      grouped[provider].push(method)
    })
    
    // Ordenar métodos dentro de cada provider
    Object.keys(grouped).forEach(provider => {
      grouped[provider].sort((a, b) => {
        // Por tipo de pagamento
        const typeA = a.payment_type || ''
        const typeB = b.payment_type || ''
        if (typeA !== typeB) {
          return typeA.localeCompare(typeB)
        }
        // Por parcelas
        const installmentsA = a.installments || 0
        const installmentsB = b.installments || 0
        if (installmentsA !== installmentsB) {
          return installmentsA - installmentsB
        }
        // Por bandeira
        const brandA = a.card_brand || ''
        const brandB = b.card_brand || ''
        return brandA.localeCompare(brandB)
      })
    })
    
    return grouped
  }

  // Obter providers únicos, ordenando com DINHEIRO primeiro
  const getProviders = () => {
    const providers = new Set()
    paymentMethods.forEach(method => {
      if (method.provider) {
        providers.add(method.provider)
      }
    })
    return Array.from(providers).sort((a, b) => {
      if (a === 'DINHEIRO') return -1
      if (b === 'DINHEIRO') return 1
      return a.localeCompare(b)
    })
  }

  // Verificar se método já foi selecionado
  const isMethodSelected = (methodId) => {
    return selectedPayments.some(p => p.id === methodId)
  }

  // Confirmar pagamento
  const handleConfirm = () => {
    // Prevenir múltiplas submissões
    if (submitting) return
    
    setError('')

    if (selectedPayments.length === 0) {
      setError('Selecione pelo menos uma forma de recebimento')
      return
    }

    const totalPayments = calculateTotalPayments()
    const difference = calculateDifference()

    // Permitir diferença de até R$ 0,01 (arredondamento)
    if (Math.abs(difference) > 0.01) {
      setError(`O valor total dos pagamentos (${formatCurrency(totalPayments)}) não corresponde ao valor da venda (${formatCurrency(totalAmount)})`)
      return
    }

    // Validar números de autorização antes de preparar dados
    for (const p of selectedPayments) {
      const authNumber = authorizationNumbers[p.id]?.trim()
      if (authNumber && (authNumber.length < 4 || authNumber.length > 100)) {
        setError('Número de autorização deve ter entre 4 e 100 caracteres')
        return
      }
    }

    // Preparar dados para enviar
    const paymentData = selectedPayments.map(p => {
      const method = paymentMethods.find(m => m.id === p.id)
      const isCash = method && isCashMethod(method)
      const authNumber = authorizationNumbers[p.id]?.trim() || null
      
      // Para dinheiro, usar o valor a descontar, não o valor recebido
      let amount = parseCurrency(p.amount)
      if (isCash && cashToDiscount) {
        amount = parseCurrency(cashToDiscount)
      }
      
      const paymentObj = {
        paymentReceiptMethodId: p.id,
        paymentMethodId: p.id,
        amount: amount,
        requiresConfirmation: p.requiresConfirmation,
        paymentMethodName: p.name || method?.name || 'Não informado',
        name: p.name || method?.name || 'Não informado',
        authorizationNumber: authNumber || null
      }
      
      // Adicionar dados de dinheiro se for método de dinheiro
      if (isCash) {
        paymentObj.isCash = true
        paymentObj.cashReceived = cashReceived ? parseCurrency(cashReceived) : null
        paymentObj.cashToDiscount = cashToDiscount ? parseCurrency(cashToDiscount) : amount
      }
      
      return paymentObj
    })

    setSubmitting(true)
    if (onConfirm) {
      onConfirm(paymentData)
    }
  }

  // Limpar ao fechar
  const handleClose = () => {
    if (!required) {
      setSelectedPayments([])
      setAuthorizationNumbers({})
      setError('')
      setSubmitting(false)
      onClose()
    } else {
      // Mostrar modal de confirmação
      setConfirmCancel(true)
    }
  }

  // Confirmar cancelamento
  const handleConfirmCancel = () => {
    setSelectedPayments([])
    setAuthorizationNumbers({})
    setCashReceived('')
    setCashToDiscount('')
    setError('')
    setSubmitting(false)
    setConfirmCancel(false)
    onClose()
  }

  // Resetar estado quando o modal abrir
  useEffect(() => {
    if (isOpen) {
      setSubmitting(false)
      setError('')
      setSelectedPayments([])
      setAuthorizationNumbers({})
      setSelectedProvider(null)
      setCashReceived('')
      setCashToDiscount('')
      setConfirmCancel(false)
    }
  }, [isOpen])

  // Componente de visualização hierárquica de providers
  const ProviderHierarchyView = ({
    paymentMethods,
    selectedPayments,
    onAddPayment,
    getPaymentIcon,
    selectedProvider,
    setSelectedProvider,
    groupMethodsByProvider,
    getProviders,
    isMethodSelected
  }) => {
    const grouped = groupMethodsByProvider()
    const providers = getProviders()

    const toggleProvider = (provider) => {
      if (selectedProvider === provider) {
        setSelectedProvider(null)
      } else {
        setSelectedProvider(provider)
      }
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {providers.map(provider => {
          const isExpanded = selectedProvider === provider
          const methodsInProvider = grouped[provider] || []
          const availableMethods = methodsInProvider.filter(m => !isMethodSelected(m.id))

          return (
            <div key={provider} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
              {/* Header do Provider */}
              <div
                onClick={() => toggleProvider(provider)}
                style={{
                  padding: '1rem',
                  background: isExpanded ? '#f3f4f6' : '#f9fafb',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  fontWeight: 600,
                  fontSize: '1rem',
                  transition: 'background 0.2s',
                  borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isExpanded) e.currentTarget.style.background = '#f3f4f6'
                }}
                onMouseLeave={(e) => {
                  if (!isExpanded) e.currentTarget.style.background = '#f9fafb'
                }}
              >
                {isExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                <span>{provider}</span>
                <span style={{ marginLeft: 'auto', fontSize: '0.875rem', fontWeight: 'normal', color: '#6b7280' }}>
                  {availableMethods.length} forma(s) disponível(is)
                </span>
              </div>

              {/* Métodos do Provider */}
              {isExpanded && (
                <div style={{ padding: '0.75rem', background: 'white' }}>
                  {availableMethods.length === 0 ? (
                    <p style={{ padding: '1rem', color: '#6b7280', textAlign: 'center', fontSize: '0.875rem' }}>
                      Todas as formas deste provedor já foram selecionadas.
                    </p>
                  ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
                      {availableMethods.map(method => {
                        const Icon = getPaymentIcon(method.name)
                        return (
                          <button
                            key={method.id}
                            onClick={() => onAddPayment(method)}
                            style={{
                              padding: '1rem',
                              border: '1px solid #e5e7eb',
                              borderRadius: '6px',
                              background: 'white',
                              cursor: 'pointer',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              gap: '0.5rem',
                              transition: 'all 0.2s',
                              textAlign: 'center'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = '#f9fafb'
                              e.currentTarget.style.borderColor = '#1e3a8a'
                              e.currentTarget.style.transform = 'translateY(-2px)'
                              e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)'
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = 'white'
                              e.currentTarget.style.borderColor = '#e5e7eb'
                              e.currentTarget.style.transform = 'translateY(0)'
                              e.currentTarget.style.boxShadow = 'none'
                            }}
                          >
                            <Icon size={24} style={{ color: '#1e3a8a' }} />
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', width: '100%' }}>
                              <span style={{ fontWeight: 500, fontSize: '0.875rem' }}>{method.name}</span>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center' }}>
                                {method.payment_type && <span>{method.payment_type}</span>}
                                {method.installments && <span>{method.installments}x</span>}
                                {method.card_brand && <span>{method.card_brand}</span>}
                              </div>
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  if (!isOpen) return null

  const totalPayments = calculateTotalPayments()
  const difference = calculateDifference()
  const isExact = Math.abs(difference) <= 0.01
  return (
    <div className="erp-modal-overlay" onClick={required ? undefined : handleClose}>
      <div 
        className="erp-modal erp-modal--medium payment-method-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="erp-modal__header">
          <h2 className="erp-modal__title">Selecionar Forma de Recebimento</h2>
          {!required && (
            <button type="button" className="erp-modal__close" onClick={handleClose}>×</button>
          )}
        </div>

        <div className="erp-modal__body">
          {error && (
            <div className="payment-error">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          <div className="payment-summary">
            <div className="summary-item">
              <span>Valor da Venda:</span>
              <strong>{formatCurrency(totalAmount)}</strong>
            </div>
            <div className="summary-item">
              <span>Total dos Pagamentos:</span>
              <strong>{formatCurrency(totalPayments)}</strong>
            </div>
            <div className={`summary-item ${isExact ? 'exact' : 'difference'}`}>
              <span>{isExact ? '✓ Valor correto' : 'Diferença:'}</span>
              <strong>{formatCurrency(Math.abs(difference))}</strong>
            </div>
          </div>

          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              Carregando formas de recebimento...
            </div>
          ) : (
            <>
              {/* Formas de pagamento selecionadas */}
              {selectedPayments.length > 0 && (
                <div className="selected-payments">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <h3>Formas Selecionadas</h3>
                    {selectedPayments.length > 1 && (
                      <button
                        type="button"
                        onClick={handleAutoFillRemaining}
                        style={{
                          padding: '0.5rem 1rem',
                          background: '#f3f4f6',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          cursor: 'pointer',
                          color: '#374151',
                          fontWeight: 500
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#e5e7eb'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#f3f4f6'
                        }}
                      >
                        Preencher Restante no Último
                      </button>
                    )}
                  </div>
                  {selectedPayments.map(payment => {
                    const Icon = getPaymentIcon(payment.name)
                    const method = paymentMethods.find(m => m.id === payment.id)
                    const isCash = method && isCashMethod(method)
                    const cashChangeValue = isCash ? calculateCashChange() : null

                    return (
                      <div key={payment.id} className="selected-payment-item">
                        <div className="payment-info">
                          <Icon size={20} />
                          <span className="payment-name">{payment.name}</span>
                        </div>
                        
                        {isCash ? (
                          /* Campos específicos para dinheiro */
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                              <div style={{ minWidth: 0 }}>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                                  Valor Recebido
                                </label>
                                <input
                                  type="text"
                                  value={cashReceived}
                                  onChange={(e) => handleCashReceivedChange(e.target.value)}
                                  onBlur={(e) => {
                                    const value = e.target.value
                                    if (!value || value === '') {
                                      setCashReceived('')
                                    } else {
                                      const numValue = parseCurrency(value)
                                      setCashReceived(formatCurrency(numValue))
                                    }
                                  }}
                                  placeholder="0,00"
                                  className="amount-input"
                                  style={{ width: '100%', minWidth: '140px' }}
                                />
                              </div>
                              <div style={{ minWidth: 0 }}>
                                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, color: '#374151', marginBottom: '0.5rem' }}>
                                  Valor a Descontar
                                </label>
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'stretch' }}>
                                  <input
                                    type="text"
                                    value={cashToDiscount}
                                    onChange={(e) => handleCashToDiscountChange(e.target.value)}
                                    onBlur={(e) => {
                                      const value = e.target.value
                                      if (!value || value === '') {
                                        handleAutoFillCashDiscount()
                                      } else {
                                        const numValue = parseCurrency(value)
                                        const formatted = formatCurrency(numValue)
                                        setCashToDiscount(formatted)
                                        
                                        const cashPayment = selectedPayments.find(p => p.id === payment.id)
                                        if (cashPayment) {
                                          setSelectedPayments(selectedPayments.map(p => 
                                            p.id === payment.id ? { ...p, amount: formatted } : p
                                          ))
                                        }
                                      }
                                    }}
                                    placeholder="0,00"
                                    className="amount-input"
                                    style={{ flex: '1 1 auto', minWidth: '140px', width: '100%' }}
                                  />
                                  <button
                                    type="button"
                                    onClick={handleAutoFillCashDiscount}
                                    style={{
                                      padding: '0.625rem 1rem',
                                      background: '#3b82f6',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '6px',
                                      fontSize: '0.8125rem',
                                      cursor: 'pointer',
                                      fontWeight: 600,
                                      whiteSpace: 'nowrap',
                                      transition: 'background 0.2s',
                                      flexShrink: 0
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#2563eb'
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = '#3b82f6'
                                    }}
                                    title="Preencher com valor restante"
                                  >
                                    Auto
                                  </button>
                                </div>
                              </div>
                            </div>
                            {cashChangeValue !== null && cashChangeValue > 0 && (
                              <div style={{
                                padding: '1rem',
                                background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                                border: '2px solid #fbbf24',
                                borderRadius: '8px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                              }}>
                                <span style={{ fontWeight: 700, color: '#92400e', fontSize: '0.9375rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span>💰</span> Troco:
                                </span>
                                <strong style={{ color: '#92400e', fontSize: '1.25rem', fontWeight: 700 }}>{formatCurrency(cashChangeValue)}</strong>
                              </div>
                            )}
                          </div>
                        ) : (
                          /* Campos para outros métodos de pagamento */
                          <>
                            <div className="payment-amount">
                              <input
                                type="text"
                                value={payment.amount}
                                onChange={(e) => handleUpdateAmount(payment.id, e.target.value)}
                                onBlur={(e) => handleAmountBlur(payment.id, e.target.value)}
                                placeholder="0,00"
                                className="amount-input"
                              />
                            </div>
                            <div className="payment-authorization">
                              <input
                                type="text"
                                value={authorizationNumbers[payment.id] || ''}
                                onChange={(e) => handleUpdateAuthorizationNumber(payment.id, e.target.value)}
                                placeholder="Nº Autorização (opcional)"
                                className="authorization-input"
                                maxLength={100}
                              />
                              <small className="authorization-hint">Opcional - Número retornado pela maquininha</small>
                            </div>
                          </>
                        )}
                        
                        <button
                          className="remove-payment-btn"
                          onClick={() => handleRemovePayment(payment.id)}
                          title="Remover"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )
                  })}
                </div>
              )}

              {/* Lista de formas disponíveis com hierarquia */}
              <div className="available-payments">
                <h3>Formas Disponíveis</h3>
                {paymentMethods.length === 0 ? (
                  <p style={{ padding: '1rem', color: '#6b7280', textAlign: 'center' }}>
                    Nenhuma forma de recebimento cadastrada. Configure em Financeiro → Formas de Recebimento.
                  </p>
                ) : (
                  <ProviderHierarchyView
                    paymentMethods={paymentMethods}
                    selectedPayments={selectedPayments}
                    onAddPayment={handleAddPayment}
                    getPaymentIcon={getPaymentIcon}
                    selectedProvider={selectedProvider}
                    setSelectedProvider={setSelectedProvider}
                    groupMethodsByProvider={groupMethodsByProvider}
                    getProviders={getProviders}
                    isMethodSelected={isMethodSelected}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <div className="erp-modal__footer">
          <button
            className="btn-secondary"
            onClick={handleClose}
            disabled={required && selectedPayments.length > 0}
          >
            {required && selectedPayments.length > 0 ? 'Concluir Pagamento' : 'Cancelar'}
          </button>
          <button
            className="btn-primary"
            onClick={handleConfirm}
            disabled={!isExact || selectedPayments.length === 0 || loading || submitting}
          >
            {submitting ? 'Processando...' : 'Confirmar Pagamento'}
          </button>
        </div>
      </div>

      {/* Modal de confirmação de cancelamento */}
      <ConfirmModal
        isOpen={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={handleConfirmCancel}
        title="Cancelar Pagamento"
        message="Deseja cancelar o pagamento?"
        type="warning"
        confirmText="Sim, cancelar"
        cancelText="Não"
      />
    </div>
  )
}

export default PaymentMethodModal

